import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  ReferenceLine,
} from "recharts";
import {
  TrendingUp, TrendingDown, Minus, Activity, Tag, RotateCcw,
  Clock, Search, Sparkles, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Loader2, Zap, Trophy, AlertCircle,
} from "lucide-react";

/* ───── Myntra palette (matches AppShell light theme) ───── */
const C = {
  pink: "#FF3F6C",
  pinkSoft: "#FFE3EA",
  orange: "#FF905A",
  ink: "#282C3F",
  sub: "#535665",
  mute: "#94969F",
  line: "#EDEDF1",
  bg: "#FAFAFB",
  card: "#FFFFFF",
  green: "#03A685",
  greenSoft: "#E0F4EE",
  red: "#FF3F6C",
  redSoft: "#FFE3EA",
  blue: "#3766E0",
  purple: "#7C3AED",
  amber: "#F59E0B",
};

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const cmpct = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return "₹" + (v / 10000000).toFixed(1) + "Cr";
  if (v >= 100000) return "₹" + (v / 100000).toFixed(1) + "L";
  if (v >= 1000) return "₹" + (v / 1000).toFixed(1) + "K";
  return "₹" + v;
};

/* ───── Reusable card ───── */
function Card({ title, subtitle, icon: Icon, accent = C.pink, action, children, testid, isDemo, demoReason }) {
  return (
    <section
      className="rounded-2xl bg-white border p-5 sm:p-6"
      style={{ borderColor: C.line, boxShadow: "0 1px 0 rgba(15,23,42,0.02)" }}
      data-testid={testid}
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div
            className="h-10 w-10 rounded-xl grid place-items-center shrink-0"
            style={{ background: accent + "1A", color: accent }}
          >
            {Icon ? <Icon size={18} /> : null}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-base font-bold" style={{ color: C.ink }}>{title}</div>
              {isDemo && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: C.pinkSoft, color: C.pink, border: `1px solid ${C.pink}33` }}
                  title={demoReason || "Demo data"}
                >
                  <Sparkles size={10} /> Demo
                </span>
              )}
              {isDemo === false && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: C.greenSoft, color: C.green, border: `1px solid ${C.green}33` }}
                  title="Built from your uploaded files"
                >
                  <Zap size={10} /> Live from your uploads
                </span>
              )}
            </div>
            {subtitle && (
              <div className="text-xs mt-0.5" style={{ color: C.sub }}>{subtitle}</div>
            )}
          </div>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function Stat({ label, value, hint, trend, testid, style: styleOverride }) {
  const positive = trend > 0;
  const negative = trend < 0;
  return (
    <div
      className="rounded-xl px-4 py-3 border"
      style={{ borderColor: C.line, background: "#FBFBFC", ...(styleOverride || {}) }}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: C.mute }}>{label}</div>
      <div className="text-xl font-extrabold mt-1" style={{ color: C.ink }}>{value}</div>
      {(hint || trend != null) && (
        <div className="flex items-center gap-1 text-[11px] mt-1">
          {positive && <ArrowUpRight size={12} style={{ color: C.green }} />}
          {negative && <ArrowDownRight size={12} style={{ color: C.red }} />}
          <span style={{ color: positive ? C.green : negative ? C.red : C.sub }}>
            {hint}{trend != null ? ` ${trend > 0 ? "+" : ""}${trend}%` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

/* ───── Tooltip ───── */
function TT({ active, payload, label, suffix = "", isCurrency = false }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs"
      style={{ background: "#FFFFFFEE", border: `1px solid ${C.line}`, color: C.ink, boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}
    >
      <div className="font-bold mb-1" style={{ color: C.ink }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span style={{ color: C.sub }}>{p.name}:</span>
          <span className="font-bold" style={{ color: C.ink }}>
            {isCurrency ? cmpct(p.value) : Number(p.value).toLocaleString("en-IN")}{suffix}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ───── Main page ───── */
export default function GrowthAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [veloRange, setVeloRange] = useState("daily"); // daily | weekly | monthly
  const [forecastWindow, setForecastWindow] = useState(30); // 30 | 60 | 90

  useEffect(() => {
    api
      .get("/seller/analytics/growth")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <AppShell role="seller">
        <div className="flex items-center gap-2" style={{ color: C.sub }}>
          <Loader2 size={16} className="animate-spin" /> Loading Growth Analytics…
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell role="seller">
        <div style={{ color: C.ink }}>Couldn't load analytics. Please refresh.</div>
      </AppShell>
    );
  }

  return (
    <AppShell role="seller">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: C.pink }}>
              Growth Analytics
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mt-1" style={{ color: C.ink }}>
              See what's selling, what's coming, what to fix.
            </h1>
            <p className="text-sm mt-1 max-w-2xl" style={{ color: C.sub }}>
              Five high-value modules in one view — velocity, returns,
              best time slots, keyword rank and revenue forecast.
            </p>
          </div>
          {data.is_demo ? (
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: C.pinkSoft, color: C.pink, border: `1px solid ${C.pink}33` }}
              data-testid="demo-badge"
              title="No uploads yet — upload a sales CSV/XLSX to switch to real data."
            >
              <Sparkles size={12} /> All demo · upload data to go live
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: C.greenSoft, color: C.green, border: `1px solid ${C.green}33` }}
              data-testid="live-badge"
              title={`Live data from your ${data.uploads_count || 0} upload(s)`}
            >
              <Zap size={12} /> {data.real_sections}/5 sections live from your uploads
            </div>
          )}
        </div>

        {data.is_demo && (
          <div
            className="rounded-2xl border p-4 sm:p-5 flex items-start gap-3"
            style={{ borderColor: C.line, background: "#FBFBFC" }}
            data-testid="no-uploads-banner"
          >
            <AlertCircle size={18} style={{ color: C.amber, marginTop: 2 }} />
            <div className="text-sm" style={{ color: C.sub }}>
              <span className="font-bold" style={{ color: C.ink }}>You're viewing sample data.</span>{" "}
              Upload your Myntra / Nykaa / Ajio sales report (CSV or XLSX) on{" "}
              <a href="/seller/upload" className="font-bold" style={{ color: C.pink }}>Upload Data</a>{" "}
              and this page instantly switches to your real sales velocity, returns, time-slots and revenue forecast.{" "}
              Keyword rank will stay sample until live marketplace access is enabled.
            </div>
          </div>
        )}

        {/* 1. Sales Velocity */}
        <VelocityCard data={data.sales_velocity} range={veloRange} setRange={setVeloRange} />

        {/* 2. Returns (Competitor Price Monitoring permanently removed per product decision) */}
        <ReturnsCard data={data.returns} />

        {/* 3. Best Time Slots */}
        <TimeSlotsCard data={data.time_slots} />

        {/* 4. Keyword Ranks */}
        <KeywordsCard data={data.keywords} />

        {/* 5. Revenue Forecast */}
        <ForecastCard data={data.forecast} window={forecastWindow} setWindow={setForecastWindow} />
      </div>
    </AppShell>
  );
}

/* ──────────────────────────────────────────────────────────
   1. Sales Velocity Tracker
   ────────────────────────────────────────────────────────── */
function VelocityCard({ data, range, setRange }) {
  const { daily_timeline, skus, totals } = data;

  // Resample the daily timeline into weekly buckets (sum each platform per
  // ISO-week-start) or monthly buckets (group by YYYY-MM) so the toggle
  // actually changes what the chart shows.
  const chartData = React.useMemo(() => {
    if (range === "daily" || !Array.isArray(daily_timeline)) return daily_timeline || [];
    const buckets = new Map();
    const platforms = ["Myntra", "Nykaa", "Ajio", "Amazon", "Flipkart"];
    for (const row of daily_timeline) {
      const d = new Date(row.date);
      if (isNaN(d.getTime())) continue;
      let key;
      if (range === "monthly") {
        key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      } else {
        // weekly — bucket by Monday-start week
        const day = d.getUTCDay() || 7;
        const monday = new Date(d);
        monday.setUTCDate(d.getUTCDate() - (day - 1));
        key = `W-${monday.toISOString().slice(0, 10)}`;
      }
      const existing = buckets.get(key) || { date: key, Myntra: 0, Nykaa: 0, Ajio: 0, Amazon: 0, Flipkart: 0 };
      for (const p of platforms) existing[p] = (existing[p] || 0) + (Number(row[p]) || 0);
      buckets.set(key, existing);
    }
    return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [daily_timeline, range]);

  // Sort the per-SKU table by the active range column so the top movers for
  // that period bubble to the top.
  const sortedSkus = React.useMemo(() => {
    const list = [...(skus || [])];
    list.sort((a, b) => (Number(b[range]) || 0) - (Number(a[range]) || 0));
    return list;
  }, [skus, range]);

  // Show the headline stat for the active range first; keep the other two
  // visible but visually demoted.
  const statOrder = range === "daily"
    ? ["today", "week", "month"]
    : range === "weekly"
      ? ["week", "today", "month"]
      : ["month", "week", "today"];
  const statMap = {
    today: { label: "Today",         value: totals.today_units, hint: "units sold today" },
    week:  { label: "Last 7 days",   value: totals.week_units,  hint: "units sold this week" },
    month: { label: "Last 30 days",  value: totals.month_units, hint: "units sold this month" },
  };

  const rangeLabel = range === "daily" ? "Daily" : range === "weekly" ? "Weekly" : "Monthly";

  return (
    <Card
      title="Sales Velocity Tracker"
      subtitle={`${rangeLabel} units sold across every marketplace.`}
      icon={Activity}
      accent={C.pink}
      testid="card-velocity"
      isDemo={data.is_demo}
      action={
        <div className="flex rounded-lg border" style={{ borderColor: C.line }}>
          {["daily", "weekly", "monthly"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className="px-3 py-1.5 text-xs font-bold capitalize"
              style={{
                background: range === r ? C.pink : "transparent",
                color: range === r ? "#FFFFFF" : C.sub,
              }}
              data-testid={`velocity-range-${r}`}
            >
              {r}
            </button>
          ))}
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" data-testid={`velocity-stats-${range}`}>
        {statOrder.map((k, i) => {
          const s = statMap[k];
          const active = i === 0;
          return (
            <Stat
              key={k}
              label={s.label}
              value={Number(s.value || 0).toLocaleString()}
              hint={s.hint}
              testid={`velo-${k}`}
              style={active ? { borderColor: C.pink, boxShadow: `0 12px 30px -16px ${C.pink}` } : undefined}
            />
          );
        })}
      </div>

      <div className="h-72">
        <ResponsiveContainer>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="velMyntra" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.pink} stopOpacity={0.35} />
                <stop offset="95%" stopColor={C.pink} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="velNykaa" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.purple} stopOpacity={0.30} />
                <stop offset="95%" stopColor={C.purple} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="velAjio" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.blue} stopOpacity={0.30} />
                <stop offset="95%" stopColor={C.blue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.line} />
            <XAxis
              dataKey="date"
              tick={{ fill: C.mute, fontSize: 10 }}
              tickFormatter={(v) => {
                if (range === "weekly") return `Wk ${String(v).replace("W-", "")}`;
                if (range === "monthly") return String(v);
                return String(v).slice(5);
              }}
            />
            <YAxis tick={{ fill: C.mute, fontSize: 10 }} />
            <Tooltip content={<TT suffix=" units" />} />
            <Legend wrapperStyle={{ fontSize: 11, color: C.sub }} />
            <Area type="monotone" dataKey="Myntra" stroke={C.pink} fill="url(#velMyntra)" strokeWidth={2} />
            <Area type="monotone" dataKey="Nykaa"  stroke={C.purple} fill="url(#velNykaa)" strokeWidth={2} />
            <Area type="monotone" dataKey="Ajio"   stroke={C.blue} fill="url(#velAjio)" strokeWidth={2} />
            <Area type="monotone" dataKey="Amazon"   stroke={C.orange} fill="transparent" strokeWidth={1.5} />
            <Area type="monotone" dataKey="Flipkart" stroke={C.green}  fill="transparent" strokeWidth={1.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Per-SKU leaderboard — sorted by the active range */}
      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="velocity-sku-table">
          <thead>
            <tr style={{ color: C.mute }} className="text-[10px] uppercase tracking-wider">
              <th className="text-left font-bold py-2">SKU</th>
              <th className="text-left font-bold py-2">Style ID</th>
              <th className="text-left font-bold py-2">Name</th>
              <th className="text-right font-bold py-2" style={{ color: range === "daily" ? C.pink : C.mute }}>Daily</th>
              <th className="text-right font-bold py-2" style={{ color: range === "weekly" ? C.pink : C.mute }}>Weekly</th>
              <th className="text-right font-bold py-2" style={{ color: range === "monthly" ? C.pink : C.mute }}>Monthly</th>
              <th className="text-right font-bold py-2">Trend</th>
            </tr>
          </thead>
          <tbody>
            {sortedSkus.map((s) => {
              return (
                <tr key={s.sku} className="border-t" style={{ borderColor: C.line, color: C.ink }}>
                  <td className="py-2.5 font-mono text-xs" style={{ color: C.sub }}>{s.sku}</td>
                  <td className="py-2.5 font-mono text-xs" style={{ color: C.indigo || "#4F46E5" }}>
                    {s.style_id || "—"}
                  </td>
                  <td className="py-2.5 font-semibold">{s.name}</td>
                  <td className="py-2.5 text-right" style={{ fontWeight: range === "daily" ? 800 : 500, color: range === "daily" ? C.pink : C.ink }}>{s.daily}</td>
                  <td className="py-2.5 text-right" style={{ fontWeight: range === "weekly" ? 800 : 500, color: range === "weekly" ? C.pink : C.ink }}>{s.weekly}</td>
                  <td className="py-2.5 text-right" style={{ fontWeight: range === "monthly" ? 800 : 500, color: range === "monthly" ? C.pink : C.ink }}>{s.monthly}</td>
                  <td className="py-2.5 text-right">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
                      style={{
                        background: s.trend === "up" ? C.greenSoft : s.trend === "down" ? C.redSoft : "#F2F2F4",
                        color: s.trend === "up" ? C.green : s.trend === "down" ? C.red : C.sub,
                      }}
                    >
                      {s.trend === "up" && <TrendingUp size={11} />}
                      {s.trend === "down" && <TrendingDown size={11} />}
                      {s.trend === "flat" && <Minus size={11} />}
                      {s.delta_pct > 0 ? "+" : ""}{s.delta_pct}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────
   2. Competitor Price Monitoring
   ────────────────────────────────────────────────────────── */
function CompetitorCard({ data }) {
  const sub = data.empty
    ? "Awaiting your Myntra sales upload"
    : data.source
      ? `${data.source} · ${data.tracked_skus} SKUs`
      : `Live tracking — Myntra portal · ${data.tracked_skus} SKUs · same category as your uploads`;
  return (
    <Card
      title="Competitor Price Monitoring"
      subtitle={sub}
      icon={Tag}
      accent={C.orange}
      testid="card-competitor"
      isDemo={false}
      demoReason={null}
    >
      {data.empty ? (
        <div className="rounded-xl p-6 text-center" style={{ background: "#FBFBFC", border: `1px dashed ${C.line}` }}>
          <Tag size={28} style={{ color: C.orange, margin: "0 auto 8px" }} />
          <div className="text-sm font-bold" style={{ color: C.ink }}>Upload your Myntra sales Excel</div>
          <div className="text-xs mt-1 max-w-md mx-auto leading-relaxed" style={{ color: C.sub }}>
            We tap into the Myntra portal and pull live competitor prices for the
            <b> exact categories </b> you sell — only after you upload your sales file.
            No synthetic data, ever.
          </div>
          <a
            href="/seller/upload"
            className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: C.pink, color: "#FFFFFF" }}
            data-testid="competitor-upload-cta"
          >
            Upload sales file →
          </a>
        </div>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {data.items.map((it) => (
          <div
            key={it.name}
            className="rounded-xl p-4 border"
            style={{ borderColor: C.line, background: "#FBFBFC" }}
            data-testid={`comp-row-${it.name.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="font-bold text-sm" style={{ color: C.ink }}>{it.name}</div>
                {it.style_id && (
                  <div className="text-[10px] font-mono mt-0.5" style={{ color: C.indigo || "#4F46E5" }}>
                    Style ID · {it.style_id}
                  </div>
                )}
                <div className="text-[11px] mt-0.5" style={{ color: C.sub }}>
                  Your price <span className="font-bold" style={{ color: C.ink }}>{inr(it.our_price)}</span>
                  {" · "}Median {inr(it.median_competitor)}
                </div>
              </div>
              <span
                className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full"
                style={{
                  background: it.stance === "premium" ? C.pinkSoft : it.stance === "cheaper" ? C.greenSoft : "#FFF4E6",
                  color: it.stance === "premium" ? C.pink : it.stance === "cheaper" ? C.green : C.amber,
                }}
              >
                {it.stance}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {it.competitors.map((c, i) => (
                <div key={i} className="rounded-lg p-2 border" style={{ borderColor: C.line, background: "#FFFFFF" }}>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: C.mute }}>{c.platform}</div>
                  <div className="font-bold text-xs mt-0.5" style={{ color: C.ink }}>{c.brand}</div>
                  <div className="flex items-baseline justify-between mt-1">
                    <span className="text-sm font-extrabold" style={{ color: C.ink }}>{inr(c.price)}</span>
                    <span
                      className="text-[10px] font-bold"
                      style={{ color: c.delta_vs_us < 0 ? C.green : c.delta_vs_us > 0 ? C.red : C.sub }}
                    >
                      {c.delta_vs_us > 0 ? "+" : ""}{inr(c.delta_vs_us)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 text-[11px] flex items-center gap-1" style={{ color: C.sub }}>
              <Sparkles size={11} style={{ color: C.pink }} />
              AI suggests pricing at <span className="font-bold ml-1" style={{ color: C.pink }}>{inr(it.suggested_price)}</span>
              <span className="ml-1" style={{ color: C.mute }}>(closer to median).</span>
            </div>
          </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────
   3. Return Rate Analysis
   ────────────────────────────────────────────────────────── */
function ReturnsCard({ data }) {
  const pieData = data.reason_split.slice(0, 5);
  const COLORS = [C.pink, C.orange, C.amber, C.purple, C.blue];
  return (
    <Card
      title="Return Rate Analysis"
      subtitle={`Avg return rate ${data.avg_return_rate_pct}% vs industry ${data.industry_benchmark_pct}%.`}
      icon={RotateCcw}
      accent={data.avg_return_rate_pct > data.industry_benchmark_pct ? C.red : C.green}
      testid="card-returns"
      isDemo={data.is_demo}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: C.mute }}>
            Top reasons (all SKUs)
          </div>
          <div className="h-52">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="pct" nameKey="reason" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<TT suffix="%" />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {pieData.map((r, i) => (
              <div key={r.reason} className="flex items-center gap-1.5 text-[11px]" style={{ color: C.sub }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                {r.reason} · <span className="font-bold" style={{ color: C.ink }}>{r.pct}%</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: C.mute }}>
            High-risk SKUs
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="return-sku-list">
            {data.rows.slice(0, 6).map((r) => (
              <div
                key={r.sku}
                className="rounded-lg px-3 py-2 border flex items-center justify-between"
                style={{ borderColor: C.line, background: "#FFFFFF" }}
              >
                <div>
                  <div className="text-sm font-bold" style={{ color: C.ink }}>{r.name}</div>
                  <div className="text-[11px]" style={{ color: C.sub }}>
                    {r.returns}/{r.orders} returned · {r.top_reasons[0].reason}
                  </div>
                </div>
                <span
                  className="text-xs font-extrabold px-2 py-1 rounded-md"
                  style={{
                    background: r.risk === "high" ? C.redSoft : r.risk === "medium" ? "#FFF4E6" : C.greenSoft,
                    color: r.risk === "high" ? C.red : r.risk === "medium" ? C.amber : C.green,
                  }}
                >
                  {r.return_rate_pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────
   4. Best Selling Time Slots
   ────────────────────────────────────────────────────────── */
function TimeSlotsCard({ data }) {
  // Build matrix
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const max = Math.max(...data.heatmap.map((c) => c.orders));
  const cell = (day, hour) => data.heatmap.find((c) => c.day === day && c.hour === hour)?.orders || 0;
  const colorFor = (v) => {
    const t = Math.min(1, v / max);
    // Myntra pink fade
    const a = 0.08 + t * 0.92;
    return `rgba(255, 63, 108, ${a})`;
  };

  return (
    <Card
      title="Best Selling Time Slots"
      subtitle={`Peak: ${data.peak_day.day} · ${String(data.peak_hour.hour).padStart(2, "0")}:00`}
      icon={Clock}
      accent={C.blue}
      testid="card-time-slots"
      isDemo={data.is_demo}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <Stat label="Peak day" value={data.peak_day.day} hint={`${data.peak_day.orders.toLocaleString()} orders`} testid="peak-day" />
        <Stat label="Peak hour" value={`${String(data.peak_hour.hour).padStart(2, "0")}:00`} hint={`${data.peak_hour.orders.toLocaleString()} orders`} testid="peak-hour" />
        <Stat label="Best window" value="19:00 – 22:00" hint="evening burst" testid="best-window" />
      </div>

      {/* Heatmap */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full" data-testid="heatmap">
          <div className="grid" style={{ gridTemplateColumns: `60px repeat(24, minmax(22px, 1fr))`, gap: 3 }}>
            <div />
            {hours.map((h) => (
              <div key={h} className="text-[9px] text-center font-bold" style={{ color: C.mute }}>{h}</div>
            ))}
            {days.map((d) => (
              <React.Fragment key={d}>
                <div className="text-[11px] font-bold flex items-center" style={{ color: C.sub }}>{d}</div>
                {hours.map((h) => {
                  const v = cell(d, h);
                  return (
                    <div
                      key={d + h}
                      title={`${d} ${String(h).padStart(2, "0")}:00 — ${v} orders`}
                      className="rounded-[3px] aspect-square"
                      style={{ background: colorFor(v) }}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* By-hour line */}
      <div className="mt-5 h-44">
        <ResponsiveContainer>
          <LineChart data={data.by_hour} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.line} />
            <XAxis dataKey="hour" tick={{ fill: C.mute, fontSize: 10 }} />
            <YAxis tick={{ fill: C.mute, fontSize: 10 }} />
            <Tooltip content={<TT suffix=" orders" />} />
            <Line type="monotone" dataKey="orders" stroke={C.blue} strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────
   5. Keyword Ranking Tracker
   ────────────────────────────────────────────────────────── */
function KeywordsCard({ data }) {
  return (
    <Card
      title="Keyword Ranking Tracker"
      subtitle={`${data.tracked} keywords tracked · ${data.in_top10} in top 10 · ${data.improving} improving.`}
      icon={Search}
      accent={C.purple}
      testid="card-keywords"
      isDemo={data.is_demo}
      demoReason={data.demo_reason}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="keyword-table">
          <thead>
            <tr style={{ color: C.mute }} className="text-[10px] uppercase tracking-wider">
              <th className="text-left font-bold py-2">Keyword</th>
              <th className="text-left font-bold py-2">Platform</th>
              <th className="text-right font-bold py-2">Rank</th>
              <th className="text-right font-bold py-2">Δ 30d</th>
              <th className="text-right font-bold py-2">Volume</th>
              <th className="text-left font-bold py-2 pl-3">Trend</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.keyword} className="border-t" style={{ borderColor: C.line, color: C.ink }}>
                <td className="py-2.5 font-semibold">
                  <div className="flex items-center gap-2">
                    {r.in_top10 && <Trophy size={12} style={{ color: C.amber }} />}
                    {r.keyword}
                  </div>
                </td>
                <td className="py-2.5 text-xs" style={{ color: C.sub }}>{r.platform}</td>
                <td className="py-2.5 text-right">
                  <span
                    className="inline-flex items-center font-extrabold px-2 py-0.5 rounded-md text-xs"
                    style={{
                      background: r.current_rank <= 10 ? C.greenSoft : r.current_rank <= 30 ? "#FFF4E6" : C.redSoft,
                      color: r.current_rank <= 10 ? C.green : r.current_rank <= 30 ? C.amber : C.red,
                    }}
                  >
                    #{r.current_rank}
                  </span>
                </td>
                <td className="py-2.5 text-right">
                  <span
                    className="inline-flex items-center gap-0.5 text-xs font-bold"
                    style={{ color: r.delta > 0 ? C.green : r.delta < 0 ? C.red : C.sub }}
                  >
                    {r.delta > 0 && <ArrowUpRight size={11} />}
                    {r.delta < 0 && <ArrowDownRight size={11} />}
                    {r.delta > 0 ? "+" : ""}{r.delta}
                  </span>
                </td>
                <td className="py-2.5 text-right text-xs" style={{ color: C.sub }}>
                  {r.search_volume.toLocaleString()}/mo
                </td>
                <td className="py-2.5 pl-3" style={{ minWidth: 120 }}>
                  <div className="h-7 w-28">
                    <ResponsiveContainer>
                      <LineChart data={r.history} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                        <YAxis hide domain={[1, 120]} reversed />
                        <Line
                          type="monotone"
                          dataKey="rank"
                          stroke={r.delta > 0 ? C.green : r.delta < 0 ? C.red : C.purple}
                          strokeWidth={1.8}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────
   6. Revenue Forecast
   ────────────────────────────────────────────────────────── */
function ForecastCard({ data, window, setWindow }) {
  const combined = useMemo(() => {
    const slice = data.forecast.slice(0, window).map((p) => ({
      date: p.date,
      forecast: p.revenue,
      lower: p.lower,
      upper: p.upper,
    }));
    const hist = data.history.map((p) => ({ date: p.date, actual: p.revenue }));
    return [...hist, ...slice];
  }, [data, window]);

  const totalKey = window === 30 ? "next_30" : window === 60 ? "next_60" : "next_90";
  return (
    <Card
      title="Revenue Forecasting"
      subtitle={`${data.model} · ${data.confidence_pct}% model confidence.`}
      icon={Zap}
      accent={C.green}
      testid="card-forecast"
      isDemo={data.is_demo}
      action={
        <div className="flex rounded-lg border" style={{ borderColor: C.line }}>
          {[30, 60, 90].map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className="px-3 py-1.5 text-xs font-bold"
              style={{
                background: window === w ? C.green : "transparent",
                color: window === w ? "#FFFFFF" : C.sub,
              }}
              data-testid={`forecast-window-${w}`}
            >
              {w}d
            </button>
          ))}
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Last 30 days" value={cmpct(data.totals.prev_30)} hint="actual revenue" testid="fc-prev30" />
        <Stat label="Next 30 days" value={cmpct(data.totals.next_30)} hint="forecast" testid="fc-next30" />
        <Stat label="Next 60 days" value={cmpct(data.totals.next_60)} hint="forecast" testid="fc-next60" />
        <Stat label="Growth (30d)" value={`${data.totals.growth_pct}%`} trend={data.totals.growth_pct} hint="vs prev 30" testid="fc-growth" />
      </div>

      <div className="h-80">
        <ResponsiveContainer>
          <AreaChart data={combined} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="fcActual" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.green} stopOpacity={0.35} />
                <stop offset="100%" stopColor={C.green} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fcForecast" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.pink} stopOpacity={0.30} />
                <stop offset="100%" stopColor={C.pink} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fcBand" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.pink} stopOpacity={0.10} />
                <stop offset="100%" stopColor={C.pink} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.line} />
            <XAxis dataKey="date" tick={{ fill: C.mute, fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: C.mute, fontSize: 10 }} tickFormatter={cmpct} />
            <Tooltip content={<TT isCurrency />} />
            <Legend wrapperStyle={{ fontSize: 11, color: C.sub }} />
            <ReferenceLine x={data.history[data.history.length - 1]?.date} stroke={C.mute} strokeDasharray="4 4" label={{ value: "Today", fill: C.sub, fontSize: 10, position: "top" }} />
            <Area type="monotone" dataKey="upper" stroke="transparent" fill="url(#fcBand)" name="Upper band" />
            <Area type="monotone" dataKey="lower" stroke="transparent" fill="#FFFFFF" name="Lower band" />
            <Area type="monotone" dataKey="actual" stroke={C.green} fill="url(#fcActual)" strokeWidth={2.5} name="Actual" />
            <Area type="monotone" dataKey="forecast" stroke={C.pink} fill="url(#fcForecast)" strokeWidth={2.5} strokeDasharray="6 3" name="Forecast" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div
        className="mt-4 rounded-xl border p-3 flex items-start gap-2"
        style={{ borderColor: C.line, background: "#FBFBFC" }}
      >
        <AlertCircle size={14} style={{ color: C.amber, marginTop: 2 }} />
        <div className="text-[12px]" style={{ color: C.sub }}>
          <span className="font-bold" style={{ color: C.ink }}>Insight:</span> Based on the
          last 30 days, projected revenue for the next {window} days is{" "}
          <span className="font-bold" style={{ color: C.ink }}>{cmpct(data.totals[totalKey])}</span>.
          Lock in inventory by end of week to capture the upper-band scenario.
        </div>
      </div>
    </Card>
  );
}
